import { ArrowLeft, MapPin, Satellite, Sun, Zap, Wind, AlertTriangle, AlertCircle, Image as ImageIcon, Building2, Users, Folder, User, Mail, Phone, Activity } from 'lucide-react';
import { motion } from 'framer-motion';
import { useState, useEffect, useRef, useContext } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SolarPanel } from '@/components/systems/SolarPanel';
import { ChargerPanel } from '@/components/systems/ChargerPanel';
import { AQIPanel } from '@/components/systems/AQIPanel';
import { HVACPanel } from '@/components/systems/HVACPanel';
import { StarlinkPanel } from '@/components/systems/StarlinkPanel';
import { formatTimestamp, cn } from '@/lib/utils';
import { useDarkMode } from '@/hooks/useDarkMode';
import { MAP_STYLES } from '@/components/map/mapConfig';
import { getLocationTextClass } from '@/lib/locationUtils';
import { isValidLocation } from '@/components/map/mapConfig';
import { LocationSourceBadge } from './LocationSourceBadge';
import { LocationFixTime } from './LocationFixTime';
import { UnitDetailQuickStats } from './UnitDetailQuickStats';
import type { Unit, StarlinkSystem } from '@/types';
import { useEffectiveRate } from '@/hooks/useEffectiveRate';
import { EffectiveRateDisplay } from '@/components/settings/EffectiveRateDisplay';
import { useUnitCheckinStatus } from '@/hooks/useUnitCheckinStatus';
import { CheckinStatusBadge } from '@/components/checkins/CheckinStatusBadge';
import { UnitQRCode } from '@/components/checkins/UnitQRCode';
import { RBACContext } from '@/contexts/RBACContext';
import { UnitFaultsSection, type UnitFaultsAnchor } from './UnitFaultsSection';

// Helper to create placeholder Starlink data when no real data is available
const createPlaceholderStarlinkData = (unitName: string): StarlinkSystem => ({
  enabled: false,
  connectionStatus: 'not_activated',
  signalStrength: 0,
  signalQuality: 0,
  noiseLevel: 0,
  currentMetrics: {
    downloadSpeed: 0,
    uploadSpeed: 0,
    pingLatency: 0,
    packetLoss: 0,
    timestamp: new Date().toISOString(),
  },
  metricsHistory: Array(24).fill(null).map((_, i) => ({
    downloadSpeed: 0,
    uploadSpeed: 0,
    pingLatency: 0,
    packetLoss: 0,
    timestamp: new Date(Date.now() - (23 - i) * 3600000).toISOString(),
  })),
  totalClients: 0,
  wifiClients: [],
  clients2_4GHz: 0,
  clients5GHz: 0,
  terminalId: 'N/A',
  firmwareVersion: 'N/A',
  hardwareVersion: 'N/A',
  uptime: 0,
  lastReboot: new Date().toISOString(),
  ssid: unitName,
  channel2_4GHz: 0,
  channel5GHz: 0,
  wifiEnabled: false,
});

interface UnitDetailViewProps {
  unit: Unit;
  onBack: () => void;
  onOpenGallery?: () => void;
  onSystemUpdate?: (system: string, settings: Record<string, unknown>) => void;
  onTabChange?: (tab: 'solar' | 'charger' | 'hvac' | 'starlink' | 'aqi') => void;
  initialTab?: 'solar' | 'charger' | 'hvac' | 'starlink' | 'aqi';
  scrollToTabs?: boolean;
}

/**
 * Render a detailed view for a unit including header, status badge, location preview, image gallery, point-of-contact card, and system panels (Solar, Charger, HVAC, Starlink).
 *
 * @param unit - Unit data used to populate all parts of the view
 * @param onBack - Called when the user requests to navigate back
 * @param onOpenGallery - Optional callback invoked to open the unit's image gallery
 * @param onSystemUpdate - Optional callback invoked when a system's settings are updated; receives the system key (`'solar'|'charger'|'hvac'|'starlink'`) and the new settings
 * @param onTabChange - Optional callback invoked when the active tab changes; receives `'solar'|'charger'|'hvac'|'starlink'`
 * @param initialTab - Initial active tab (defaults to `'solar'`)
 * @param scrollToTabs - If true, automatically scrolls the container so the tabs section is visible when the view mounts or the unit changes
 * @returns The JSX element rendering the unit detail interface
 */
export function UnitDetailView({ unit, onBack, onOpenGallery, onSystemUpdate, onTabChange, initialTab = 'solar', scrollToTabs = false }: UnitDetailViewProps) {
  const { isDarkMode } = useDarkMode();
  // Use starlink data from unit.systems.starlink (returned by API/mock data)
  const starlinkData = unit.systems?.starlink || null;
  const isLoadingStarlink = false;

  // Fetch effective rate for this unit
  const { rate: effectiveRate, isLoading: isLoadingRate, error: rateError } = useEffectiveRate({
    unitId: unit?.id,
    autoFetch: true,
  });

  // Fetch check-in status for this unit
  const { currentStatus: checkinStatus, isLoading: isLoadingCheckin } = useUnitCheckinStatus(unit?.id);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // Device errors are staff only (Eric, 2026-11-02). Read without the throwing hook so a page
  // rendered outside the provider simply shows no section; the server is the real gate.
  const isStaff = useContext(RBACContext)?.isInternal === true;
  // "See details": the section scrolls once its rows are there (they may still be loading).
  const [faultFocus, setFaultFocus] = useState<{ anchor: UnitFaultsAnchor; seq: number } | null>(null);
  const showFaultDetails = (anchor: UnitFaultsAnchor) => {
    setFaultFocus((prev) => ({ anchor, seq: (prev?.seq ?? 0) + 1 }));
  };
  const [activeTab, setActiveTab] = useState(initialTab);
  const tabsRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  // Sync active tab when initialTab prop changes (handles external navigation like sidebar clicks)
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  // Handle auto-scroll to tabs if requested
  useEffect(() => {
    if (scrollToTabs && tabsRef.current && containerRef.current && headerRef.current) {
      // Small delay to ensure layout is stable
      setTimeout(() => {
        const tabsRect = tabsRef.current?.getBoundingClientRect();
        const headerRect = headerRef.current?.getBoundingClientRect();

        if (tabsRect && headerRect) {
          // Calculate offset to position tabs right below the header
          // Add a 16px buffer (margin space) so it's not flushed against the header
          const offset = tabsRect.top - headerRect.bottom - 16;
          
          containerRef.current?.scrollBy({
            top: offset,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [scrollToTabs, unit.id]); // Re-run when unit changes or scroll requested

  // Hardcoded POC data (temporary until database table exists)
  const mockPrimaryContact = {
    name: "Sarah Mitchell",
    role: "Operations Manager",
    email: "sarah.mitchell@hollywoodtrucks.com",
    phone: "(310) 555-0142",
  };

  // Refresh handler for StarlinkPanel (re-fetch unit data to get updated starlink data)
  const refreshStarlinkData = async () => {
    // Starlink data comes from unit.systems.starlink, updated via parent component refresh
    // This is a placeholder for future real-time data fetching
  };

  return (
    <>
    <motion.div
      ref={containerRef}
      className="w-full h-full overflow-y-auto bg-background pb-24 lg:pb-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      style={{ willChange: 'opacity' }}
    >
      {/* Header */}
      <div ref={headerRef} className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur">
        <div className="w-full px-4 lg:px-16 py-2 lg:py-4">
          {/* Mobile Layout */}
          <div className="lg:hidden flex flex-col gap-1">
            <div className="flex items-center justify-between">
              {/* Left Group: Back, Name, Status, Time */}
              <div className="flex items-center gap-1.5 overflow-hidden">
                <Button variant="ghost" size="icon" onClick={onBack} className="-ml-2 shrink-0">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="flex flex-col justify-center min-w-0">
                  <h1 className="text-lg font-bold leading-none">{unit.unit_code}</h1>
                </div>
                <Badge variant={
                  unit.status === 'online' ? 'success' :
                  unit.status === 'idle' ? 'idle' :
                  unit.status === 'offline' ? 'error' :
                  'outline'
                } className="shrink-0 px-1.5 h-5 text-[10px]">
                  {unit.status.toUpperCase()}
                </Badge>
                <CheckinStatusBadge
                  status={checkinStatus}
                  isLoading={isLoadingCheckin}
                  variant="compact"
                />
                <span className="text-xs text-muted-foreground truncate ml-1">
                  {formatTimestamp(unit.lastUpdate)}
                </span>
              </div>

              {/* Right: Starlink Icon */}
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8 shrink-0 ml-2"
                disabled={!starlinkData?.serviceLineNumber}
                onClick={() => {
                  if (starlinkData?.serviceLineNumber) {
                    window.open(`https://starlink.com/account/service-line/${starlinkData.serviceLineNumber}`, '_blank');
                  }
                }}
              >
                <Satellite className="h-4 w-4" />
              </Button>
            </div>

            {/* Row 2: Company Info */}
            <div className="pl-1">
               {(unit.company || unit.primary_group) && (
                  <div className="text-xs text-foreground flex flex-wrap gap-1 items-center">
                      {unit.company && (
                          <span className="font-medium flex items-center">
                              <Building2 className="h-3 w-3 mr-1" />
                              {unit.company.name}
                          </span>
                      )}
                      {unit.primary_group && (
                          <>
                          {unit.company && <span className="mx-1 text-[#8FB560] font-bold">•</span>}
                          <span className="font-medium flex items-center">
                              <Folder className="h-3 w-3 mr-1" />
                              {unit.primary_group.name}
                          </span>
                          </>
                      )}
                  </div>
               )}
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:block">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={onBack}>
                <ArrowLeft className="h-5 w-5" />
              </Button>

              {/* Left: Unit Name, Status & Last Update */}
              <div className="flex-shrink-0">
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold">{unit.unit_code}</h1>
                  <Badge variant={
                    unit.status === 'online' ? 'success' :
                    unit.status === 'idle' ? 'idle' :
                    unit.status === 'offline' ? 'error' :
                    'outline'
                  }>
                    {unit.status.toUpperCase()}
                  </Badge>
                  <CheckinStatusBadge
                    status={checkinStatus}
                    isLoading={isLoadingCheckin}
                  />
                  <span className="text-xs text-muted-foreground">
                    {formatTimestamp(unit.lastUpdate)}
                  </span>
                </div>
              </div>

              {/* Right: Starlink Button - Pushed to the right */}
              <Button 
                variant="outline" 
                size="sm" 
                className="text-xs flex-shrink-0 ml-auto"
                disabled={!starlinkData?.serviceLineNumber}
                onClick={() => {
                  if (starlinkData?.serviceLineNumber) {
                    window.open(`https://starlink.com/account/service-line/${starlinkData.serviceLineNumber}`, '_blank');
                  }
                }}
              >
                <span className="uppercase">Starlink</span>
                <Satellite className="h-4 w-4 ml-1" />
              </Button>
            </div>

            {/* Second Row: Company & Group Hierarchy + Quick Stats */}
            <div className="flex items-center justify-between mt-2 ml-14">
              <div className="text-sm text-foreground">
                {unit.company && (
                  <>
                    <Building2 className="inline h-3.5 w-3.5 mr-1" />
                    <span className="font-medium">{unit.company.name}</span>
                  </>
                )}
                {unit.primary_group?.parent_group && (
                  <>
                    <span className="mx-2 text-[#8FB560] font-bold">•</span>
                    <Users className="inline h-3.5 w-3.5 mr-1" />
                    <span className="font-medium">{unit.primary_group.parent_group.name}</span>
                  </>
                )}
                {unit.primary_group && (
                  <>
                    <span className="mx-2 text-[#8FB560] font-bold">•</span>
                    <Folder className="inline h-3.5 w-3.5 mr-1" />
                    <span className="font-medium">{unit.primary_group.name}</span>
                  </>
                )}
              </div>

              <div className="flex items-center gap-6">
                {/* Quick Stats - Clickable to navigate to corresponding tab (battery from live_status) */}
                <UnitDetailQuickStats unit={unit} onOpenTab={(tab) => { setActiveTab(tab); onTabChange?.(tab); }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Warning Banner - Only shown when active */}
      {(unit.warning_count ?? 0) > 0 && (
        <div className="lg:px-4 lg:py-2 py-2 px-0 bg-warning/20 lg:border lg:border-warning/50">
          <div className="w-full px-4 flex items-center gap-3">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 text-warning" />
            <p className="text-sm">
              <span className="font-medium">
                {unit.warning_count} Active Warning{unit.warning_count === 1 ? '' : 's'}
              </span>
              <span className="text-muted-foreground ml-2">
                — System requires attention.
              </span>
            </p>
            {isStaff && (
              <button
                type="button"
                onClick={() => showFaultDetails('unit-faults-warnings')}
                className="ml-auto shrink-0 whitespace-nowrap text-sm font-medium underline-offset-4 hover:underline"
              >
                See details
              </button>
            )}
          </div>
        </div>
      )}

      {/* Fault Banner - Only shown when active */}
      {(unit.fault_count ?? 0) > 0 && (
        <div className="lg:px-4 lg:py-2 py-2 px-0 bg-fault/20 lg:border lg:border-fault/50">
          <div className="w-full px-4 flex items-center gap-3">
            <AlertCircle className="h-4 w-4 flex-shrink-0 text-fault" />
            <p className="text-sm">
              <span className="font-medium">
                {unit.fault_count} Active Fault{unit.fault_count === 1 ? '' : 's'}
              </span>
              <span className="text-muted-foreground ml-2">
                — Critical system fault detected.
              </span>
            </p>
            {isStaff && (
              <button
                type="button"
                onClick={() => showFaultDetails('unit-faults-faults')}
                className="ml-auto shrink-0 whitespace-nowrap text-sm font-medium underline-offset-4 hover:underline"
              >
                See details
              </button>
            )}
          </div>
        </div>
      )}

      <div className="w-full px-4 lg:px-16 py-4 lg:py-6">
        {/* Faults and warnings, explained (staff only; renders nothing when there are none) */}
        <div className="mb-4 empty:hidden">
          <UnitFaultsSection
            unitId={unit.id}
            enabled={isStaff}
            refreshKey={`${unit.fault_count ?? 0}:${unit.warning_count ?? 0}`}
            focus={faultFocus}
          />
        </div>
        {/* Unit Image & Info */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Left Column: Image + Info Cards */}
          <div className="contents lg:block lg:col-span-1 lg:space-y-3">
            {/* Image and Button Group */}
            <div className="space-y-3 order-1 lg:order-none">
              {/* Unit Image */}
              <div
                className="aspect-video lg:aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer group relative"
                onClick={() => setLightboxOpen(true)}
              >
                <img
                  src={unit.image}
                  alt={unit.name}
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/assets/units/placeholder.jpg';
                  }}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-background/90 backdrop-blur px-3 py-1.5 rounded-full text-sm font-medium">
                    Click to view
                  </div>
                </div>
              </div>

              {/* Manage Images Button */}
              <Button
                variant="outline"
                className="w-full"
                onClick={onOpenGallery}
              >
                <ImageIcon className="h-4 w-4 mr-2" />
                Manage Images
              </Button>
            </div>

            {/* Point of Contact Card */}
            <div className="relative rounded-lg border border-border overflow-hidden bg-[#8FB560]/5 border-l-4 border-l-[#8FB560] order-3 lg:order-none">
              {/* POC Label - Top Right */}
              <div className="absolute top-2 right-2 bg-[#8FB560] text-black text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                POC
              </div>

              <div className="p-4 space-y-3">
                {/* Avatar and Info Row */}
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-[#8FB560]/20 border-3 border-[#8FB560] flex items-center justify-center overflow-hidden">
                      <User className="h-8 w-8 text-[#8FB560]" />
                    </div>
                  </div>

                  {/* Name, Role, Contact Details */}
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <div className="font-semibold text-sm">{mockPrimaryContact.name}</div>
                    {mockPrimaryContact.role && (
                      <div className="text-xs text-muted-foreground">
                        {mockPrimaryContact.role}
                      </div>
                    )}
                    {mockPrimaryContact.email && (
                      <div className="text-xs text-muted-foreground truncate" title={mockPrimaryContact.email}>
                        {mockPrimaryContact.email}
                      </div>
                    )}
                    {mockPrimaryContact.phone && (
                      <div className="text-xs text-muted-foreground">
                        {mockPrimaryContact.phone}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons - Large and Touch-Friendly */}
                <div className="grid grid-cols-2 gap-2">
                  {mockPrimaryContact.email && (
                    <a
                      href={`mailto:${mockPrimaryContact.email}`}
                      className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-[#8FB560] hover:bg-[#7FA550] text-black font-medium text-sm transition-colors active:scale-95"
                    >
                      <Mail className="h-4 w-4" />
                      <span>Email</span>
                    </a>
                  )}
                  {mockPrimaryContact.phone && (
                    <a
                      href={`tel:${mockPrimaryContact.phone.replace(/\D/g, '')}`}
                      className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-[#8FB560] hover:bg-[#7FA550] text-black font-medium text-sm transition-colors active:scale-95"
                    >
                      <Phone className="h-4 w-4" />
                      <span>Call</span>
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Location Info with Group Hierarchy */}
            <div className="rounded-lg border border-border overflow-hidden order-4 lg:order-none">
              {isValidLocation(unit.location) ? (
                <>
                  {/* Small Map Preview - Clickable to go to main map */}
                  <div
                    className="relative h-32 bg-muted cursor-pointer group"
                    onClick={onBack}
                  >
                    {/* Centered marker overlay - matches main map circular markers */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                      <div className="relative">
                        <div className={`w-4 h-4 rounded-full border-2 border-white shadow-lg ${
                          unit.status === 'online' ? 'bg-[#10b981]' :
                          unit.status === 'offline' ? 'bg-[#ef4444]' :
                          unit.status === 'idle' ? 'bg-[#9ca3af]' :
                          'bg-[#6b7280]'
                        }`} />
                      </div>
                    </div>
                    <iframe
                      src={`https://api.mapbox.com/styles/v1/${(isDarkMode ? MAP_STYLES.dark : MAP_STYLES.light).replace('mapbox://styles/', '')}/static/${unit.location.lng},${unit.location.lat},14,0/320x128@2x?access_token=${import.meta.env.VITE_MAPBOX_TOKEN}`}
                      className="w-full h-full object-cover pointer-events-none"
                      loading="lazy"
                      title="Location preview"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-background/90 backdrop-blur px-3 py-1.5 rounded-full text-xs font-medium">
                        View on map
                      </div>
                    </div>
                  </div>

                  {/* Location Details */}
                  <div className="flex items-start gap-3 p-4">
                    <MapPin className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">Current Location</span>
                        <LocationSourceBadge source={unit.locationSource} />
                      </div>
                      <div className={cn('text-sm text-muted-foreground', getLocationTextClass(unit.locationSource))}>{unit.location.address || '—'}</div>
                      <div className="text-xs text-muted-foreground mt-1 tabular-nums">
                        {unit.location.lat.toFixed(4)}, {unit.location.lng.toFixed(4)}
                      </div>
                      {/* T-021: when the position was last fixed (all sources). Renders
                          nothing until a writer first stamps location_updated_at. */}
                      <LocationFixTime at={unit.unitLocationAt} />
                    </div>
                  </div>
                </>
              ) : (
                /* No coordinates → don't render a fake 0,0 map/position. */
                <div className="flex items-start gap-3 p-4">
                  <MapPin className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">Current Location</span>
                      <LocationSourceBadge source={unit.locationSource} />
                    </div>
                    <div className="text-sm text-muted-foreground">No GPS fix</div>
                  </div>
                </div>
              )}
            </div>

            {/* Electric Rate Info */}
            <div className="order-5 lg:order-none">
              <EffectiveRateDisplay
                rate={effectiveRate}
                isLoading={isLoadingRate}
                error={rateError}
                variant="compact"
              />
            </div>

            {/* Check-In QR Code */}
            <div className="order-6 lg:order-none">
              <UnitQRCode
                unitCode={unit.unit_code}
                checkinToken={unit.checkin_token}
              />
            </div>
          </div>

          {/* Right Column: System Panels */}
          <div className="lg:col-span-3 order-2 lg:order-none" ref={tabsRef}>
            <Tabs
              value={activeTab}
              onValueChange={(value) => {
                const newTab = value as 'solar' | 'charger' | 'hvac' | 'starlink' | 'aqi';
                setActiveTab(newTab);
                onTabChange?.(newTab);
              }}
              className="w-full h-full"
            >
          <TabsList className="grid w-full grid-cols-5 h-12">
            <TabsTrigger value="solar" className="flex items-center gap-1 lg:gap-2 text-xs lg:text-sm px-0">
              <Sun className="h-5 w-5 lg:h-5 lg:w-5" />
              <span className="hidden sm:inline">Solar</span>
            </TabsTrigger>
            <TabsTrigger value="charger" className="flex items-center gap-1 lg:gap-2 text-xs lg:text-sm px-0">
              <Zap className="h-5 w-5 lg:h-5 lg:w-5" />
              <span className="hidden sm:inline">Charger</span>
            </TabsTrigger>
            <TabsTrigger value="hvac" className="flex items-center gap-1 lg:gap-2 text-xs lg:text-sm px-0">
              <Wind className="h-5 w-5 lg:h-5 lg:w-5" />
              <span className="hidden sm:inline">HVAC</span>
            </TabsTrigger>
            <TabsTrigger value="starlink" className="flex items-center gap-1 lg:gap-2 text-xs lg:text-sm px-0">
              <Satellite className="h-5 w-5 lg:h-5 lg:w-5" />
              <span className="hidden sm:inline">Starlink</span>
            </TabsTrigger>
            <TabsTrigger value="aqi" className="flex items-center gap-1 lg:gap-2 text-xs lg:text-sm px-0">
              <Activity className="h-5 w-5 lg:h-5 lg:w-5" />
              <span className="hidden sm:inline">AQI</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="solar" className="mt-4 lg:mt-6">
            {unit.systems?.solar ? (
              <SolarPanel system={unit.systems.solar} unitId={unit.id} liveStatus={unit.live_status} />
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                System data not available.
              </div>
            )}
          </TabsContent>

          <TabsContent value="charger" className="mt-4 lg:mt-6">
            {unit.systems?.charger ? (
              <ChargerPanel
                system={unit.systems.charger}
                onUpdate={(settings) => onSystemUpdate?.('charger', settings)}
              />
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                System data not available.
              </div>
            )}
          </TabsContent>

          <TabsContent value="hvac" className="mt-4 lg:mt-6">
            {unit.systems?.hvac ? (
              <HVACPanel
                system={unit.systems.hvac}
                onUpdate={(settings) => onSystemUpdate?.('hvac', settings)}
              />
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                System data not available.
              </div>
            )}
          </TabsContent>

          <TabsContent value="starlink" className="mt-4 lg:mt-6">
            {isLoadingStarlink ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">Loading Starlink telemetry...</div>
              </div>
            ) : (
              <StarlinkPanel
                system={starlinkData || createPlaceholderStarlinkData(unit.unit_code)}
                unitId={unit.id}
                onUpdate={(settings) => onSystemUpdate?.('starlink', settings)}
                onRefresh={refreshStarlinkData}
              />
            )}
          </TabsContent>

          <TabsContent value="aqi" className="mt-4 lg:mt-6">
            <AQIPanel unitId={unit.id} />
          </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </motion.div>

      {/* Lightbox for primary image */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={[
          {
            src: unit.image,
            alt: unit.name,
          },
        ]}
        styles={{
          container: { backgroundColor: 'rgba(0, 0, 0, 0.95)' },
        }}
      />
    </>
  );
}